import axios from "axios";
import config from "../../config/config";
import {
  Student,
  CreateStudentInput,
  UpdateStudentInput,
  StudentPackageModule,
  ModuleTestSessionAdmin,
  ModuleTestProgress,
} from "./common.interfaces";
import * as mocks from "./student.mocks";

const API_URL = `${config.apiBaseUrl}/admin/students`;

const axiosClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: config.defaultTimeout,
});

axiosClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Backend DTO interface (what the API actually returns)
interface AdminStudentApiDto {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  lastLoginAt?: string;
  packagesCount: number;
  overallProgress?: number;
  packages: Array<{
    packageId: string;
    packageName: string;
    purchasedAt: string;
    expiresAt?: string;
    startedAt?: string;
    percentageCompleted?: number;
  }>;
  levelTest?: {
    hasSession: boolean;
    finalLevel?: number;
    finalLevelName?: string;
    status?: string;
  };
}

interface PaginatedResponse {
  items: AdminStudentApiDto[];
  total: number;
  page: number;
  limit: number;
}

// Helper function to check if a date string is valid (not null, not default DateTime)
const isValidDate = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;
  // Check if it's the default DateTime value (0001-01-01T00:00:00)
  const date = new Date(dateString);
  return !isNaN(date.getTime()) && date.getFullYear() > 1;
};

// Helper function to format date or return fallback
const formatDateOrFallback = (dateString: string | null | undefined, fallback: string = "N/A"): string => {
  if (!isValidDate(dateString)) return fallback;
  return dateString!;
};

// Mapper function to convert backend DTO to frontend Student interface
const mapApiDtoToStudent = (dto: AdminStudentApiDto): Student => {
  const validCreatedAt = isValidDate(dto.createdAt) ? dto.createdAt! : null;
  const validLastLoginAt = isValidDate(dto.lastLoginAt) ? dto.lastLoginAt! : null;
  
  return {
    id: dto.id,
    name: dto.name,
    email: dto.email,
    status: "Active", // Backend doesn't provide status, defaulting to Active
    lastLogin: formatDateOrFallback(validLastLoginAt || validCreatedAt, "Never"),
    enrollmentDate: formatDateOrFallback(validCreatedAt, "N/A"),
    progress: {
      packagesCount: dto.packagesCount,
      overallProgress: dto.overallProgress != null ? Number(dto.overallProgress) : 0,
    },
    avatar: undefined, // Backend doesn't provide avatar
    packages: dto.packages.map((pkg) => ({
      id: pkg.packageId,
      name: pkg.packageName,
      description: "",
      level: "",
      modulesCount: 0, // Not provided by backend
      imageUrl: "",
      promotionsCount: 0, // Not provided by backend
      isActive: true,
      purchaseDate: formatDateOrFallback(pkg.purchasedAt, "N/A"),
      overallProgress: pkg.percentageCompleted != null ? Number(pkg.percentageCompleted) : 0,
      consecutiveDays: 0, // Not provided by backend
      inactiveDays: 0, // Not provided by backend
    })),
    levelTest: dto.levelTest,
  };
};

const apiFetchStudents = async (page: number = 1, limit: number = 10, searchText?: string): Promise<Student[]> => {
  try {
    let url = `/?page=${page}&limit=${limit}`;
    if (searchText && searchText.trim() !== "") {
      url += `&search=${encodeURIComponent(searchText.trim())}`;
    }
    const response = await axiosClient.get<PaginatedResponse>(url);
    // Backend returns paginated result with items array
    const items = response.data.items || [];
    return items.map(mapApiDtoToStudent);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error during get students:", error.message);
    } else {
      console.error("Unexpected error during get students:", error);
    }
    throw error;
  }
};

const apiGetStudentById = async (id: string): Promise<Student> => {
  try {
    const response = await axiosClient.get<AdminStudentApiDto>(`/${id}`);
    return mapApiDtoToStudent(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error during get student:", error.message);
    } else {
      console.error("Unexpected error during get student:", error);
    }
    throw error;
  }
};

const apiCreateStudent = async (student: CreateStudentInput): Promise<Student> => {
  try {
    // Backend only requires name, email, and optional password
    const response = await axiosClient.post<AdminStudentApiDto>("/", {
      name: student.name,
      email: student.email,
      password: undefined, // Password is optional, backend will generate if not provided
    });
    return mapApiDtoToStudent(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error during create student:", error.message);
    } else {
      console.error("Unexpected error during create student:", error);
    }
    throw error;
  }
};

const apiUpdateStudent = async (id: string, student: UpdateStudentInput): Promise<Student> => {
  try {
    const response = await axiosClient.patch<AdminStudentApiDto>(`/${id}`, {
      name: student.name,
      email: student.email,
    });
    return mapApiDtoToStudent(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error during update student:", error.message);
    } else {
      console.error("Unexpected error during update student:", error);
    }
    throw error;
  }
};

const apiDeleteStudent = async (id: string): Promise<void> => {
  try {
    await axiosClient.delete(`/${id}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error during delete student:", error.message);
    } else {
      console.error("Unexpected error during delete student:", error);
    }
    throw error;
  }
};

const apiAddPackagesToStudent = async (studentId: string, packageIds: string[]): Promise<Student> => {
  try {
    const response = await axiosClient.post<AdminStudentApiDto>(`/${studentId}/packages`, { packageIds });
    return mapApiDtoToStudent(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error during add packages:", error.message);
    } else {
      console.error("Unexpected error during add packages:", error);
    }
    throw error;
  }
};

const apiRemovePackageFromStudent = async (studentId: string, packageId: string): Promise<Student> => {
  try {
    const response = await axiosClient.delete<AdminStudentApiDto>(`/${studentId}/packages/${packageId}`);
    return mapApiDtoToStudent(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error during remove package:", error.message);
    } else {
      console.error("Unexpected error during remove package:", error);
    }
    throw error;
  }
};

const apiResetStudentPassword = async (studentId: string): Promise<Student> => {
  try {
    const response = await axiosClient.post<AdminStudentApiDto>(`/${studentId}/reset-password`);
    return mapApiDtoToStudent(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error during password reset:", error.message);
    } else {
      console.error("Unexpected error during password reset:", error);
    }
    throw error;
  }
};

const apiGetStudentPackageModules = async (
  studentId: string,
  packageId: string
): Promise<StudentPackageModule[]> => {
  const response = await axiosClient.get<StudentPackageModule[]>(
    `/${studentId}/packages/${packageId}/modules`
  );
  return response.data ?? [];
};

const apiResetModuleAnswers = async (
  studentId: string,
  moduleId: string
): Promise<void> => {
  await axiosClient.post(`/${studentId}/modules/${moduleId}/reset-answers`);
};

const apiSetStudentModuleLock = async (
  studentId: string,
  packageId: string,
  moduleId: string,
  isUnlocked: boolean
): Promise<void> => {
  await axiosClient.patch(`/${studentId}/packages/${packageId}/modules/${moduleId}/lock`, {
    isUnlocked,
  });
};

const mockGetStudentPackageModules = async (
  _studentId: string,
  _packageId: string
): Promise<StudentPackageModule[]> => {
  await new Promise((r) => setTimeout(r, 400));
  return [
    {
      moduleId: "mod-1",
      title: "Module 1: Introduction",
      internalName: "introduction",
      order: 1,
      progressPercentage: 75,
      exercisesAnswered: 6,
      totalExercises: 8,
      lastActivityAt: new Date().toISOString(),
      sessionStatus: "Active",
      isUnlocked: true,
      moduleTestProgress: ModuleTestProgress.Completed,
    },
    {
      moduleId: "mod-2",
      title: "Module 2: Basics",
      internalName: "basics",
      order: 2,
      progressPercentage: 100,
      exercisesAnswered: 10,
      totalExercises: 10,
      lastActivityAt: new Date(Date.now() - 86400000).toISOString(),
      sessionStatus: "Completed",
      isUnlocked: true,
      moduleTestProgress: ModuleTestProgress.Completed,
    },
    {
      moduleId: "mod-3",
      title: "Module 3: Practice",
      internalName: "practice",
      order: 3,
      progressPercentage: 0,
      exercisesAnswered: 0,
      totalExercises: 12,
      lastActivityAt: null,
      sessionStatus: "Abandoned",
      isUnlocked: false,
      moduleTestProgress: ModuleTestProgress.InProgress,
    },
  ];
};

const mockResetModuleAnswers = async (
  _studentId: string,
  _moduleId: string
): Promise<void> => {
  await new Promise((r) => setTimeout(r, 300));
};

const apiGetStudentModuleTestSession = async (
  studentId: string,
  moduleId: string
): Promise<ModuleTestSessionAdmin> => {
  const response = await axiosClient.get<ModuleTestSessionAdmin>(
    `/${studentId}/modules/${moduleId}/module-test-session`
  );
  return response.data;
};

const apiDeleteStudentModuleTestSessions = async (
  studentId: string,
  moduleId: string
): Promise<void> => {
  await axiosClient.delete(`/${studentId}/modules/${moduleId}/module-test-sessions`);
};

const mockGetStudentModuleTestSession = async (
  studentId: string,
  moduleId: string
): Promise<ModuleTestSessionAdmin> => {
  await new Promise((r) => setTimeout(r, 300));
  return {
    id: "mock-mt-session-1",
    studentId,
    moduleId,
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    finished: false,
    scorePercent: null,
    passed: null,
    completedAt: null,
    answers: [],
    unlockedAfterPass: [],
  };
};

export const fetchStudents = async (page?: number, limit?: number, searchText?: string): Promise<Student[]> => {
  return config.useRealApi ? apiFetchStudents(page, limit, searchText) : mocks.fetchStudents(page, limit);
};

export const getStudentById = async (id: string): Promise<Student> => {
  return config.useRealApi ? apiGetStudentById(id) : mocks.getStudentById(id);
};

export const createStudent = async (student: CreateStudentInput): Promise<Student> => {
  return config.useRealApi ? apiCreateStudent(student) : mocks.createStudent(student);
};

export const updateStudent = async (id: string, student: UpdateStudentInput): Promise<Student> => {
  return config.useRealApi ? apiUpdateStudent(id, student) : mocks.updateStudent(id, student);
};

export const deleteStudent = async (id: string): Promise<void> => {
  return config.useRealApi ? apiDeleteStudent(id) : mocks.deleteStudent(id);
};

export const addPackagesToStudent = async (studentId: string, packageIds: string[]): Promise<Student> => {
  return config.useRealApi 
    ? apiAddPackagesToStudent(studentId, packageIds) 
    : mocks.addPackagesToStudent(studentId, packageIds);
};

export const removePackageFromStudent = async (studentId: string, packageId: string): Promise<Student> => {
  return config.useRealApi 
    ? apiRemovePackageFromStudent(studentId, packageId) 
    : mocks.removePackageFromStudent(studentId, packageId);
};

export const resetStudentPassword = async (studentId: string): Promise<Student> => {
  return config.useRealApi 
    ? apiResetStudentPassword(studentId)
    : mocks.resetStudentPassword(studentId);
};

export const getStudentPackageModules = async (
  studentId: string,
  packageId: string
): Promise<StudentPackageModule[]> => {
  return config.useRealApi
    ? apiGetStudentPackageModules(studentId, packageId)
    : mockGetStudentPackageModules(studentId, packageId);
};

export const resetModuleAnswers = async (
  studentId: string,
  moduleId: string
): Promise<void> => {
  return config.useRealApi
    ? apiResetModuleAnswers(studentId, moduleId)
    : mockResetModuleAnswers(studentId, moduleId);
};

export const setStudentModuleLock = async (
  studentId: string,
  packageId: string,
  moduleId: string,
  isUnlocked: boolean
): Promise<void> => {
  if (!config.useRealApi) {
    await new Promise((r) => setTimeout(r, 200));
    return;
  }
  await apiSetStudentModuleLock(studentId, packageId, moduleId, isUnlocked);
};

export const getStudentModuleTestSession = async (
  studentId: string,
  moduleId: string
): Promise<ModuleTestSessionAdmin> => {
  return config.useRealApi
    ? apiGetStudentModuleTestSession(studentId, moduleId)
    : mockGetStudentModuleTestSession(studentId, moduleId);
};

export const deleteStudentModuleTestSessions = async (
  studentId: string,
  moduleId: string
): Promise<void> => {
  if (!config.useRealApi) {
    await new Promise((r) => setTimeout(r, 200));
    return;
  }
  await apiDeleteStudentModuleTestSessions(studentId, moduleId);
};

export type GrantChainItem = {
  id: string;
  source: string;
  sourceId: string;
  durationDays: number;
  validFrom: string;
  validUntil: string | null;
  status: string;
  note?: string | null;
  revokedReason?: string | null;
};

export type GrantChainResponse = {
  accessUntil?: string | null;
  nextBillingAt?: string | null;
  grants: GrantChainItem[];
};

const adminApi = axios.create({
  baseURL: `${config.apiBaseUrl}/admin`,
  timeout: config.defaultTimeout,
});

adminApi.interceptors.request.use((req) => {
  const token = localStorage.getItem('token');
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

export const getStudentPackageGrantChain = async (
  studentId: string,
  packageId: string
): Promise<GrantChainResponse> => {
  const response = await adminApi.get<GrantChainResponse>(
    `/students/${studentId}/packages/${packageId}/chain`
  );
  return response.data;
};

export const grantStudentPackageAccess = async (
  studentId: string,
  packageId: string,
  durationDays: number,
  note?: string
): Promise<{ success: boolean; message?: string; expiresAt?: string }> => {
  const idempotencyKey = `admin-grant:${studentId}:${packageId}:${durationDays}:${Date.now()}`;
  const response = await adminApi.post(
    `/students/${studentId}/grants`,
    { packageId, durationDays, note },
    { headers: { 'Idempotency-Key': idempotencyKey } }
  );
  return response.data;
};

export const revokeAccessGrant = async (grantId: string, reason: string): Promise<void> => {
  await adminApi.post(`/grants/${grantId}/revoke`, { reason });
};
